import { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";

import styles from "./PatientProfileNew.module.scss";

// Components
import StyledContainer from "../../../components/styled-container/StyledContainer";
import CustomButton from "../../../components/v2/custom-button/CustomButton";
import DueDateCard from "../../../components/due-date-card/DueDateCard";
import BackgroundLayout from "../../../components/custom-backgrounds/BackgroundLayout";
import OptOutModal from "../OptOutModal";
import DataLoader from "../../../components/custom-data-loader/DataLoader";
import DraggableItemSkeleton from "../../../components/custom-drag-drop/CustomDragDropSkeleton";
import CustomDragDdrop from "../../../components/custom-drag-drop/CustomDragDrop";

// API & Hooks
import { useAppSelector } from "../../../store/hooks";
import { useTranslation } from "react-i18next";
import { useUpdatePatientProfileMutation } from "../../../pages/opt-in-opt-out/services/PatientConsentAPISlice";
import { useGetDragDropListQuery } from "../../../components/custom-drag-drop/services/GetDragDropListSlice";
import { useSaveDashoboardPreferenceMutation } from "../../welcome-overview/services/SaveDashboardPreferenceApiSlice";

// Utilities
import { dateFormatter } from "../../../utility/functions/DateUtils";
import { getOptOutMessages } from "../../../utility/functions/common-functions";
import {
  CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING,
  MESSAGE_IDS,
} from "../../../utility/constants/common-constants";

// Types
import { DragDropCardList } from "../../../utility/interfaces/carePlanInterfaces";

// Other
import update from "immutability-helper";
import { useNavigationBlock } from "../../../routes/PatientProtectedRoute";
import { useGetReasonMessagesQuery } from "../../../pages/opt-in-opt-out/services/GetReasonMessageAPISlice";
import { useLocation } from "react-router-dom";
import { useGetOnboardingUIMessagesQuery } from "../../../pages/opt-in-opt-out/services/GetOnboardingUIMessageAPISlice";

const PatientProfileNew = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.state]);

  // Redux selectors
  const {
    patientProfileInfo: {
      name,
      mmEnrollmentDate,
      patientProfileId: detailsProfileId,
      preferredLanguage,
      recentDeliveryDueDate,
      actualDeliveryDate,
      duration,
      durationText,
    },
  } = useAppSelector((state) => state.patientProfileDetails);

  const {
    patientCaseId,
    loginWorkflowInfo: { livingStatusAtBirthId },
  } = useAppSelector((state) => state.patientOnboardingStatus);

  const patientProfileId = detailsProfileId;

  // API hooks
  const [updatePatientProfile] = useUpdatePatientProfileMutation();
  const [saveDashoboardPreference] = useSaveDashoboardPreferenceMutation();
  const { data: reasonList } = useGetReasonMessagesQuery(preferredLanguage, {
    skip: !preferredLanguage,
  });
  const { data: messageList } =
    useGetOnboardingUIMessagesQuery(preferredLanguage); // Fetch the onboarding messages

  const {
    data: dragDropList,
    error: dragDropError,
    isLoading: dragDropLoading,
  } = useGetDragDropListQuery(
    {
      patientId: patientProfileId,
      preferredLanguage,
    },
    {
      selectFromResult: ({ data, ...rest }) => ({
        data: data?.appModules
          ? {
              ...data,
              appModules: [...data.appModules].sort(
                (a, b) => a.displaySequence - b.displaySequence
              ),
            }
          : data,
        ...rest,
      }),
      skip: !preferredLanguage,
    }
  );

  // State
  const [cards, setCards] = useState<DragDropCardList[]>([]);
  const [initialCards, setInitialCards] = useState<DragDropCardList[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Derived values
  const enrollmentDate = useMemo(
    () => (mmEnrollmentDate ? dateFormatter(mmEnrollmentDate) : "--"),
    [mmEnrollmentDate]
  );

  const optOutReasons = getOptOutMessages(
    reasonList?.categoryReasons || []
  ).map((message) => ({
    label: message.displayReason,
    value: message.reasonId,
  }));

  const hasChanges = useMemo(() => {
    if (initialCards.length !== cards.length) return true;
    return cards.some(
      (card, index) => card.appModuleId !== initialCards[index].appModuleId
    );
  }, [cards, initialCards]);

  // Drag & drop reordering
  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setCards((prevCards) =>
      update(prevCards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevCards[dragIndex]],
        ],
      })
    );
  }, []);

  // Modal handlers
  const handleOpenModal = useCallback(() => setModalOpen(true), []);
  const handleCloseModal = useCallback(() => setModalOpen(false), []);

  const handleOptOut = useCallback(
    async (reasonId: number) => {
      await updatePatientProfile({
        body: {
          patientProfileId,
          optInSelection: false,
          termsAgreement: false,
          updatedBy: "system",
          patientCaseId,
          optOutReasonId: reasonId,
        },
      });
    },
    [patientProfileId, patientCaseId, updatePatientProfile]
  );

  const handleSaveChanges = async () => {
    try {
      const body = cards.map((card, index) => ({
        appModuleId: card.appModuleId,
        displaySequence: index + 1,
      }));

      const response = await saveDashoboardPreference({
        body,
        patientProfileId,
        preferredLanguage,
      }).unwrap();

      setInitialCards(cards);
      setSavedChanges(true);
      enqueueSnackbar(response.apiResponse, { variant: "success" });
    } catch {
      enqueueSnackbar(t("anErrorOccurred"), { variant: "error" });
    }
  };

  const { setHasChanges, setSavedChanges } = useNavigationBlock();

  useEffect(() => {
    setHasChanges(hasChanges);
    setSavedChanges(!hasChanges); // Mark changes as unsaved whenever hasChanges is true
  }, [hasChanges, setHasChanges]);

  // Initialize cards from API
  useEffect(() => {
    if (dragDropList?.appModules?.length && cards.length === 0) {
      setCards(dragDropList?.appModules);
      setInitialCards(dragDropList?.appModules);
    }
  }, [dragDropList, cards.length]);

  // Browser tab close confirmation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  const getMessageByCode = (code: string) =>
    messageList?.messageContext?.find(
      (message) => message?.messageCode === code
    )?.content || "";

  const optOutMessage = getMessageByCode(MESSAGE_IDS.OPT_OUT);

  return (
    <BackgroundLayout
      variant={
        isMobile ? "welcomeCoverGreenMobile" : "welcomeCoverGreenDesktop"
      }
      height={{ xs: "6.37rem", sm: "6.37rem", md: "14.31rem" }}
      backgroundColor="#eaf0f0"
      showFooter={true}
    >
      <StyledContainer width={isMobile ? "auto" : "996px"} padding={0}>
        <Typography
          variant={!isMobile ? "h1" : "h3"}
          color="white"
          textAlign={{ xs: "center", md: "left" }}
        >
          {name}
        </Typography>

        <Box className={styles.dashboardContainer}>
          {/* Left Column: Preferences */}
          <Paper
            className={`${styles.section} ${styles.dashboardOrganization}`}
            sx={{
              borderRadius: 3,
              border: "1px solid #D1D1D1",
              background: "#FFF",
              boxShadow: "none",
            }}
          >
            <Typography
              variant="subtitle1"
              component="h4"
              fontWeight={700}
              mb={1}
              color="primary"
            >
              {t("dashboardOrgCardTitle")}
            </Typography>
            <Typography variant="body1" mb={2}>
              {t("dashboardOrgCardText")}
            </Typography>

            <DataLoader
              isLoading={dragDropLoading}
              error={dragDropError}
              data={dragDropList?.appModules}
              loadingComponent={<DraggableItemSkeleton />}
              emptyComponent={<Typography>No preferences found.</Typography>}
              errorComponent={
                <Typography color="error">
                  Error loading preferences.
                </Typography>
              }
            >
              {() => <CustomDragDdrop cards={cards} onMoveCard={moveCard} />}
            </DataLoader>

            <CustomButton
              sx={{ fontSize: "1rem" }}
              variant="contained"
              text={t("saveChangesBtn")}
              onClick={handleSaveChanges}
              disabled={!hasChanges}
            />
          </Paper>

          {/* Right Column: Due Date + Opt-Out */}
          <Box className={styles.sectionRightWrapper}>
            {(livingStatusAtBirthId ===
              CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING.living ||
              livingStatusAtBirthId === null) && (
              <Paper
                className={styles.dueDate}
                sx={{ boxShadow: 0, borderRadius: 3 }}
              >
                <DueDateCard
                  recentDeliveryDueDate={recentDeliveryDueDate}
                  enrollmentDate={enrollmentDate}
                  actualDeliveryDate={actualDeliveryDate}
                  duration={duration}
                  durationText={durationText}
                />
              </Paper>
            )}

            <Paper
              className={`${styles.section} ${styles.optOut}`}
              sx={{
                borderRadius: 3,
                border: "1px solid #D1D1D1",
                background: "#FFF",
                boxShadow: "none",
              }}
            >
              <Typography
                variant="subtitle1"
                component="h4"
                fontWeight={700}
                mb={1}
                color="primary"
              >
                {t("optOut")}
              </Typography>
              <Typography variant="body1" mb={2}>
                {t("optOutMsg")}
              </Typography>
              <CustomButton
                variant="outlined"
                text={t("optOut")}
                color="secondary"
                onClick={handleOpenModal}
              />
            </Paper>
          </Box>
        </Box>
      </StyledContainer>

      {/* Modals */}
      <OptOutModal
        modalOpen={modalOpen}
        onCloseModal={handleCloseModal}
        handleOptOut={handleOptOut}
        optOutReasons={optOutReasons}
        message={optOutMessage}
      />
    </BackgroundLayout>
  );
};

export default PatientProfileNew;
